/**
 * CrisisResources — always-reachable safety surface for the patient portal.
 *
 * Layered now: a per-user service-group bundle card renders at the top
 * (Samaritans / NHS / Childline / Distress Centres / etc. resolved via
 * /api/portal/me/crisis-resources) — that's what the user's specific
 * service provider has configured. The global multi-region directory
 * remains below as the always-reachable safety floor (covers travel,
 * unconfigured service groups, and is reachable even if the API errors).
 *
 * The SG card is fetched best-effort on mount; if the fetch fails or
 * returns null, the page falls back silently to the global directory.
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Stack, Button, Box, Alert, Divider, Chip } from '@mui/material';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { rawApiClient as apiClient } from '../../services/api';

const RESOURCES = [
  {
    region: 'United Kingdom',
    items: [
      { name: 'Samaritans', phone: '116 123', url: 'https://www.samaritans.org', detail: '24/7 emotional support' },
      { name: 'NHS 111', phone: '111', url: 'https://111.nhs.uk', detail: 'Urgent medical help' },
      { name: 'Shout (text)', phone: 'Text 85258', detail: 'Free 24/7 mental-health text support' }
    ]
  },
  {
    region: 'Republic of Ireland',
    items: [
      { name: 'Samaritans Ireland', phone: '116 123', url: 'https://www.samaritans.org/ireland', detail: '24/7 emotional support' },
      { name: 'Pieta House', phone: '1800 247 247', url: 'https://www.pieta.ie', detail: 'Suicide & self-harm crisis' }
    ]
  },
  {
    region: 'United States',
    items: [
      { name: '988 Suicide & Crisis Lifeline', phone: '988', url: 'https://988lifeline.org', detail: '24/7 crisis support' },
      { name: 'Crisis Text Line', phone: 'Text HOME to 741741', detail: 'Free 24/7 text support' }
    ]
  },
  {
    region: 'Australia',
    items: [
      { name: 'Lifeline Australia', phone: '13 11 14', url: 'https://www.lifeline.org.au', detail: '24/7 crisis support' },
      { name: 'Beyond Blue', phone: '1300 22 4636', url: 'https://www.beyondblue.org.au', detail: 'Mental-health support' }
    ]
  }
];

const EMERGENCY = {
  description: 'If you or someone with you is in immediate danger, contact emergency services right away.',
  numbers: [
    { label: 'UK / Ireland', value: '999' },
    { label: 'EU', value: '112' },
    { label: 'US / Canada', value: '911' },
    { label: 'Australia', value: '000' }
  ]
};

function CrisisResources() {
  const [sgBundle, setSgBundle] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/portal/me/crisis-resources');
        if (cancelled) return;
        // Backend returns { bundle: null } on miss/error — we treat both
        // the same: just don't render the SG card.
        if (res?.data?.bundle) {
          setSgBundle({
            bundle: res.data.bundle,
            audience: res.data.audience,
            language: res.data.language,
            label: res.data.label
          });
        }
      } catch {
        // Silent fallback to the global directory.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>Crisis support</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          You are not alone. The resources below are available 24/7 if you need to talk to someone now.
        </Typography>
      </Box>

      <Alert severity="error" variant="filled" icon={<LocalPhoneIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>{EMERGENCY.description}</Typography>
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
          {EMERGENCY.numbers.map((n) => (
            <Box key={n.label}>
              <Typography variant="caption">{n.label}</Typography>
              <Typography variant="h5" fontWeight={700}>{n.value}</Typography>
            </Box>
          ))}
        </Stack>
      </Alert>

      {sgBundle && (
        <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6">Your local crisis support</Typography>
              <Chip
                label={sgBundle.label || sgBundle.audience || 'recommended'}
                size="small"
                color="primary"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These are the contacts configured by your care service for clients like you.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              {[sgBundle.bundle.primary, sgBundle.bundle.secondary].filter(Boolean).map((contact) => (
                <Stack key={contact.name} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{contact.name}</Typography>
                    {contact.detail && (
                      <Typography variant="body2" color="text.secondary">{contact.detail}</Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>📞 <strong>{contact.number}</strong></Typography>
                  </Box>
                  {contact.url && (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      href={contact.url}
                      target="_blank"
                      rel="noopener"
                    >
                      Visit
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
            {sgBundle.bundle.extra && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {sgBundle.bundle.extra}
              </Typography>
            )}
            {sgBundle.bundle.emergencyText && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {sgBundle.bundle.emergencyText}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {RESOURCES.map((region) => (
        <Card key={region.region} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>{region.region}</Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              {region.items.map((item) => (
                <Stack key={item.name} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.detail}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>📞 <strong>{item.phone}</strong></Typography>
                  </Box>
                  {item.url && (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      href={item.url}
                      target="_blank"
                      rel="noopener"
                    >
                      Visit
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

export default CrisisResources;
